import { Component, type ErrorInfo, type ReactNode } from "react"
import { Navigate, Route, Routes, useLocation } from "react-router-dom"
import { useAuth } from "./auth/AuthContext"
import { AppShell, Brand } from "./components/AppShell"
import { Button, ToastViewport } from "./components/ui"
import { FamilyProvider } from "./family/FamilyContext"
import { RealtimeBridge } from "./realtime/RealtimeBridge"
import { InvitePage, LoginPage, NotFoundPage, RegisterPage } from "./pages/AuthPages"
import { InstanceAdminPage } from "./pages/InstanceAdminPage"
import { SettingsPage } from "./pages/SettingsPage"
import { EventsV1Page, FamilyV1Page, HomeV1Page, MeV1Page, TasksV1Page } from "./pages/FamilyV1Pages"
import { RecurringTaskEditPage, TaskCreatePage, TaskEditPage } from "./features/tasks/TaskEditorPage"
import { ConfigEditorPage } from "./features/settings/ConfigEditorPage"
import { UiGalleryPage } from "./pages/UiGalleryPage"

class AppErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null }
  static getDerivedStateFromError(error: Error) {
    return { error }
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Application error", error, info)
  }
  render() {
    if (!this.state.error) return this.props.children
    return (
      <div className="fatal-error">
        <Brand />
        <span>🧶</span>
        <h1>Нить запуталась</h1>
        <p>Интерфейс столкнулся с неожиданной ошибкой. Данные на сервере не пострадали.</p>
        <Button onClick={() => location.reload()}>Перезагрузить приложение</Button>
      </div>
    )
  }
}

function ProtectedApp() {
  const { user, booting } = useAuth()
  const location = useLocation()
  if (booting)
    return (
      <div className="boot-screen">
        <Brand />
        <div className="boot-loader">
          <i />
          <span>Открываем семейное пространство…</span>
        </div>
      </div>
    )
  if (!user) return <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />
  if (user.requiresTwoFactorSetup && location.pathname !== "/settings") return <Navigate to="/settings" replace />
  return (
    <FamilyProvider>
      <RealtimeBridge>
        <AppShell />
      </RealtimeBridge>
    </FamilyProvider>
  )
}

export function App() {
  const uiGalleryEnabled = import.meta.env.DEV || location.hostname === "localhost" || location.hostname.endsWith(".lab")
  return (
    <AppErrorBoundary>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/register/:invitationToken" element={<RegisterPage />} />
        <Route path="/invite" element={<InvitePage />} />
        <Route path="/invite/:token" element={<InvitePage />} />
        {uiGalleryEnabled ? <Route path="/__ui" element={<UiGalleryPage />} /> : null}
        <Route element={<ProtectedApp />}>
          <Route index element={<HomeV1Page />} />
          <Route path="tasks" element={<TasksV1Page />} />
          <Route path="tasks/new" element={<TaskCreatePage />} />
          <Route path="tasks/:occurrenceId/edit" element={<TaskEditPage />} />
          <Route path="tasks/recurring/:definitionId/edit" element={<RecurringTaskEditPage />} />
          <Route path="me" element={<MeV1Page />} />
          <Route path="notifications" element={<EventsV1Page />} />
          <Route path="family" element={<FamilyV1Page />} />
          <Route path="family/config/:kind/new" element={<ConfigEditorPage />} />
          <Route path="family/config/:kind/:id/edit" element={<ConfigEditorPage />} />
          <Route path="instance" element={<InstanceAdminPage />} />
          <Route path="settings" element={<SettingsPage />} />
        </Route>
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <ToastViewport />
    </AppErrorBoundary>
  )
}
